import { ChangeEvent, FC, useEffect, useState } from 'react';
import { Box, Checkbox, Typography } from '@mui/material';
import { FormProvider } from 'react-hook-form';
import { AxiosResponse } from 'axios';
import { Link, useLocation } from 'react-router-dom';
import logo from 'assets/img/login-logo.png';
import { FormTextField } from 'components/__v5__/FormTextField';
import { custom2, text } from 'assets/colors/colors';
import { useToast } from 'hooks';
import Loader from 'components/atoms/Loader';
import MuiButton from 'components/__v5__/Button/MuiButton';
import { APP_LINKS } from 'constant/links';
import { HyperLink } from 'components/atoms';
import { useXeroSignUpForm } from './hooks/useXeroSignUpForm';
import { Container, StyledForm } from './style';
import { IXeroSignUpFormProps } from './types';
import { useXeroSignupUrlQuery } from './api/queries';
import { useXeroFormUpdateMutation } from './api/mutation';

const XeroSignUp: FC = () => {
  const { methods } = useXeroSignUpForm();
  const toast = useToast();
  const [isMessageLoading, setIsMessageLoading] = useState(false);
  const [queryParams, setQueryParams] = useState({
    id: '',
    user_name: '',
    email: '',
  });

  const { data, isLoading } = useXeroSignupUrlQuery();
  const location = useLocation();

  const { mutate, isPending } = useXeroFormUpdateMutation({
    onSuccess: (response: AxiosResponse<{ message: string }>) => {
      methods.reset();
      setIsMessageLoading(true);
      toast.success(
        response?.data?.message ||
          "We have received your request. We'll soon contact you to provide your credentials",
      );
      setTimeout(() => {
        window.location.href = APP_LINKS.LANDING_PAGE;
        setIsMessageLoading(false);
      }, 4000);
    },
    onError: (err) => {
      const errResponse = err?.response?.data as unknown as { message: string };
      const message = errResponse?.message;
      toast.error(
        typeof message === 'string'
          ? message
          : 'Error occurred. Please try again later',
      );
    },
  });

  const onSubmit = (data: IXeroSignUpFormProps) => {
    mutate({
      id: queryParams?.id,
      email: data?.email,
      organization_name: data?.organization_name,
      user_name: data?.user_name,
      password: data?.password,
      phone_number: data?.phone_number,
    });
  };

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const id = searchParams.get('id') || '';
    const user_name = searchParams.get('name') || '';
    const email = searchParams.get('email') || '';

    setQueryParams({
      id,
      user_name,
      email,
    });

    /**
     * Redirect to XERO OAuth url, IF id doesnot exist in param
     * */
    if (!id && data?.url) {
      window.location.href = data?.url;
    } else if (user_name || email) {
      methods.reset({
        user_name,
        email,
      });
    }
  }, [location.search, data?.url, methods]);

  const handleTermsChange = (e: ChangeEvent<HTMLInputElement>) => {
    methods.setValue('isTermsAccepted', e?.target?.checked);
  };

  if (isLoading) {
    return (
      <Container>
        <Loader />
      </Container>
    );
  }

  return (
    <Container>
      <Box sx={{ width: 500, margin: 'auto' }}>
        <Box
          sx={{
            textAlign: 'center',
            marginBottom: 2,
          }}
        >
          <img className="logo" src={logo} alt="logo" />
        </Box>
        <Box>
          <FormProvider {...methods}>
            <StyledForm onSubmit={methods.handleSubmit(onSubmit)}>
              <Box
                sx={{
                  textAlign: 'center',
                  fontSize: 16,
                }}
              >
                <Typography component="h5" style={{ fontWeight: 500 }}>
                  Sign up with Xero
                </Typography>
              </Box>
              <Box marginTop={2} />

              <FormTextField
                name="user_name"
                label="Username"
                hiddenLabel={false}
                shrink={false}
                placeholder="Username"
                showError
                required
                type="text"
                control={methods.control}
                data-testid="xero-signup-input-name"
                disabled={false}
              />

              <Box height={8} />

              <FormTextField
                name="organization_name"
                label="Organization Name"
                placeholder="Organization"
                hiddenLabel={false}
                shrink={false}
                showError
                required
                type="text"
                control={methods.control}
                data-testid="xero-signup-input-org"
                disabled={false}
              />
              <Box height={8} />

              <FormTextField
                name="email"
                label="Email"
                placeholder="Email"
                hiddenLabel={false}
                shrink={false}
                showError
                required
                type="text"
                control={methods.control}
                data-testid="xero-signup-input-email"
                disabled={false}
              />
              <Box height={8} />

              <FormTextField
                name="phone_number"
                label="Phone Number"
                placeholder="Phone Number"
                hiddenLabel={false}
                shrink={false}
                showError
                type="text"
                control={methods.control}
                data-testid="xero-signup-input-phone"
                disabled={false}
              />
              <Box height={8} />

              <FormTextField
                name="password"
                label="Password"
                placeholder="Password"
                hiddenLabel={false}
                shrink={false}
                autoComplete="new-password"
                showError
                type="password"
                control={methods.control}
                data-testid="xero-signup-input-phone"
                disabled={false}
              />

              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  marginTop: 1,
                }}
              >
                <Checkbox value={false} onChange={handleTermsChange} />
                <Box sx={{ marginTop: 1, fontSize: 13 }}>
                  <Box>
                    By signing up, you confirm that you’ve read and accepted our{' '}
                    <HyperLink href={APP_LINKS.PRIVACY_POLICY}>
                      Privacy policy &nbsp;
                    </HyperLink>
                    <span>and &nbsp;</span>
                    <HyperLink href={APP_LINKS.LICENSE_AGREEMENT}>
                      Terms of use.
                    </HyperLink>
                  </Box>

                  {methods?.formState?.errors?.isTermsAccepted?.message && (
                    <Typography color="error" style={{ fontSize: 12 }}>
                      Please accept the terms and conditions.
                    </Typography>
                  )}
                </Box>
              </Box>
              <Box height={8} />

              <Box marginTop={1}>
                <MuiButton
                  type="submit"
                  disabled={isLoading || isPending || isMessageLoading}
                  fullWidth
                  sx={{
                    background: custom2.main,
                    color: text.primary,
                    textTransform: 'inherit',
                    '&:hover': {
                      background: custom2.main,
                      opacity: 0.9,
                      color: text.primary,
                    },
                  }}
                >
                  Sign Up
                </MuiButton>
              </Box>

              <Box
                sx={{
                  textAlign: 'center',
                  marginTop: 2,
                }}
              >
                <Link to="/login">
                  Already have an account?{' '}
                  <span style={{ color: custom2.main }}>Login</span>
                </Link>
              </Box>
            </StyledForm>
          </FormProvider>
        </Box>
      </Box>
    </Container>
  );
};

export default XeroSignUp;
